import React, {
  memo,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from "react";
// import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { objects, validators } from "investira.sdk";
import services from "../services";
import withMessage from "../hoc/withMessage";
import withResponseHandling from "../hoc/withResponseHandling";
import { useSelector, useDispatch } from "react-redux";
// import { acCarteiraSelected } from "../store/actions";

// Context
const RecursoGarantidorContext = React.createContext();

const PAGE_SIZE = 25;

const initialState = {
  tipo: "entidade", //entidade | carteira
  recursos: [],
  recursoSelected: {},
  dialog: null, // add || edit || delete || merge | null
  isLoading: false,
  isRefetching: false,
  pagination: {
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  },
  rowCount: 0,
  rowSelection: {},
};

// Reducers
const reducers = (state = initialState, action) => {
  if (action.type === "RECURSOS_RESETED") {
    return initialState;
  }
  if (action.type === "RECURSOS_CHANGED") {
    return {
      ...state,
      recursos: action.payload,
    };
  }
  if (action.type === "RECURSO_SELECTED_CHANGED") {
    return {
      ...state,
      recursoSelected: action.payload,
    };
  }
  if (action.type === "RECURSOS_DIALOG_CHANGED") {
    return {
      ...state,
      dialog: action.payload,
    };
  }
  if (action.type === "RECURSOS_IS_LOADING_CHANGED") {
    return {
      ...state,
      isLoading: action.payload,
    };
  }
  if (action.type === "RECURSOS_IS_REFETCHING_CHANGED") {
    return {
      ...state,
      isRefetching: action.payload,
    };
  }
  if (action.type === "RECURSOS_PAGINATION_CHANGED") {
    return {
      ...state,
      pagination: action.payload,
    };
  }
  if (action.type === "RECURSOS_ROW_COUNT_CHANGED") {
    return {
      ...state,
      rowCount: action.payload,
    };
  }
  if (action.type === "RECURSOS_ROW_SELECTION_CHANGED") {
    return {
      ...state,
      rowSelection: action.payload,
    };
  }
};

// Dispatches
const actions = {
  acRecursosReseted: () => ({
    type: "RECURSOS_RESETED",
  }),
  acRecursosChanged: (pString) => ({
    type: "RECURSOS_CHANGED",
    payload: pString,
  }),
  acRecursoSelectedChanged: (pString) => ({
    type: "RECURSO_SELECTED_CHANGED",
    payload: pString,
  }),
  acRecursoDialogChanged: (pString) => ({
    type: "RECURSOS_DIALOG_CHANGED",
    payload: pString,
  }),
  acRecursosIsLoadingChanged: (pBool) => ({
    type: "RECURSOS_IS_LOADING_CHANGED",
    payload: pBool,
  }),
  acRecursosIsRefetchingChanged: (pBool) => ({
    type: "RECURSOS_IS_REFETCHING_CHANGED",
    payload: pBool,
  }),
  acPaginationChanged: (pObj) => ({
    type: "RECURSOS_PAGINATION_CHANGED",
    payload: pObj,
  }),
  acRowCountChanged: (pNum) => ({
    type: "RECURSOS_ROW_COUNT_CHANGED",
    payload: pNum,
  }),
  acRowSelectionChanged: (pObj) => ({
    type: "RECURSOS_ROW_SELECTION_CHANGED",
    payload: pObj,
  }),
};

// Custom Hook
export const useRecursoGarantidorContext = () =>
  React.useContext(RecursoGarantidorContext);

export const RecursoGarantidorProvider = memo((props) => {
  // const location = useLocation();
  // const navigate = useNavigate();
  // const storeDispatch = useDispatch();

  const request = useMemo(() => {
    return {
      entidade: services.entidades,
      carteira: services.carteiras,
    };
  }, []);

  const entidadeId = useSelector(
    (store) => store.entidade.current.default_entidade_id
  );

  const [state, dispatch] = useReducer(reducers, initialState);

  const handleRecursosChanged = (pArr) => {
    dispatch(actions.acRecursosChanged(pArr));
  };

  const handleRecursoSelectedChanged = (pObj) => {
    dispatch(actions.acRecursoSelectedChanged(pObj));
  };

  const handleDialogChanged = (pStr) => {
    dispatch(actions.acRecursoDialogChanged(pStr));
  };

  const handleDialogClosed = () => {
    dispatch(actions.acRecursoDialogChanged(null));
  };

  const handlePaginationChanged = (pFunc) => {
    const xNewValue = pFunc(state.pagination);
    dispatch(actions.acPaginationChanged(xNewValue));
  };

  const handleRowCountChanged = (pNum) => {
    dispatch(actions.acRowCountChanged(pNum));
  };

  const handleRowSelectionChanged = (pFunc) => {
    const xNewValue = pFunc(state.rowSelection);
    dispatch(actions.acRowSelectionChanged(xNewValue));
  };

  const handleBulkDelete = (pSeletedRows) => {
    const xRows = pSeletedRows.map((xRow) => {
      return xRow.original;
    });

    const xPromises = Object.values(xRows).map((xRow) => {
      const { data } = xRow;
      return deleteRecurso({ data });
    });

    Promise.all(xPromises)
      .then(() => {
        props.onMessageSuccess(`Recursos excluídos com sucesso`);
        let xRecursos = objects.deepCopy(state.recursos);

        xRecursos = xRecursos.filter((xRecurso) => {
          return !Object.values(xRows).some((xRow) => {
            return (
              xRecurso.ativo_id === xRow.ativo_id &&
              xRecurso.carteira_id === xRow.carteira_id &&
              xRecurso.data === xRow.data &&
              xRecurso.ativo_tipo_id === xRow.ativo_tipo_id
            );
          });
        });

        handleRecursosChanged(xRecursos);
      })
      .catch(() => {
        props.onMessageError(`Ocorreu uma falha durante a exclusão do recurso`);
      });
  };

  const readRecursos = useCallback((pPayload, pResolve, pReject, pFinally) => {
    const { tipo, ...restPayload } = pPayload;

    if (!state.recursos.length) {
      dispatch(actions.acRecursosIsLoadingChanged(true));
    } else {
      dispatch(actions.acRecursosIsRefetchingChanged(true));
    }

    request[tipo].pl.list(
      { ...restPayload },
      (rRes) => {
        handleRecursosChanged(Object.values(rRes.data));
        rRes?.pages?.total_items &&
          handleRowCountChanged(rRes.pages.total_items);
        pResolve && pResolve(rRes);
      },
      (rErr) => {
        props.onMessageError(rErr.message);
        pReject && pReject(rErr);
      },
      () => {
        dispatch(actions.acRecursosIsLoadingChanged(false));
        dispatch(actions.acRecursosIsRefetchingChanged(false));
      }
    );
  }, []);

  function addRecurso(pPayload = {}, pResolve, pReject, pFinally) {
    const { tipo, ...restPayload } = pPayload;
    request[tipo].pl.add(
      { ...restPayload },
      (rRes) => {
        return pResolve && pResolve(rRes);
      },
      (rErr) => {
        props.onMessageError(`Ocorreu uma falha durante a inclusão do recurso`);
        pReject && pReject(rErr);
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  const updateRecurso = useCallback(
    (pPayload = {}, pResolve, pReject, pFinally) => {
      const { tipo, ...restPayload } = pPayload;

      return request[tipo].pl.modify(
        { ...restPayload },
        (rRes) => {
          return pResolve && pResolve(rRes);
        },
        (rErr) => {
          return pReject && pReject(rErr);
        },
        () => {
          pFinally && pFinally();
        }
      );
    },
    [request]
  );

  function deleteRecurso(pData, pResolve, pReject, pFinally) {
    request[state.tipo].pl.remove(
      pData,
      (rRes) => {
        pResolve && pResolve(rRes);
      },
      (rErr) => {
        pReject && pReject(rErr);
      },
      () => {
        pFinally && pFinally();
      }
    );
  }

  useEffect(() => {
    if (props.tipo === "entidade") {
      const xPage = state.pagination.pageIndex + 1;

      readRecursos({
        sort: "data desc",
        page: xPage,
        size: state.pagination.pageSize,
      });
    }
  }, [
    props.tipo,
    readRecursos,
    state.pagination.pageIndex,
    state.pagination.pageSize,
    entidadeId,
  ]);

  useEffect(() => {
    if (props.tipo === "carteira") {
      props.carteiraId &&
        readRecursos({
          tipo: "carteira",
          carteira_id: props.carteiraId,
        });
    }
  }, [
    props.tipo,
    props.carteiraId,
    readRecursos,
    state.pagination.pageIndex,
    state.pagination.pageSize,
    entidadeId,
  ]);

  useEffect(() => {
    if (props.tipo === "carteira") {
      const xRecurso = state.recursos.find(
        (xItem) =>
          xItem.carteira_id === props.carteiraId &&
          dayjs(xItem.data).isSame(dayjs(props.dataPosicao))
      );

      handleRecursoSelectedChanged(xRecurso || {});
    }
  }, [
    props.tipo,
    state.recursos,
    props.carteiraId,
    props.dataPosicao,
    entidadeId,
  ]);

  return (
    <RecursoGarantidorContext.Provider
      value={{
        state: {
          ...state,
          ...props,
        },
        actions: {
          handleRecursosChanged,
          handleRecursoSelectedChanged,
          handleDialogChanged,
          handleDialogClosed,
          handlePaginationChanged,
          handleRowCountChanged,
          handleRowSelectionChanged,
          handleBulkDelete,
          readRecursos,
          addRecurso,
          updateRecurso,
          deleteRecurso,
        },
      }}
    >
      {props.children}
    </RecursoGarantidorContext.Provider>
  );
});

RecursoGarantidorProvider.displayName = "RecursoGarantidorProvider";

export default withMessage(withResponseHandling(RecursoGarantidorProvider));
